import { StrictMode, type ReactNode } from "react"
import { createRoot } from "react-dom/client"

import App from "./App.tsx"
import "./index.css"
import { AppConfigProvider, fetchAppConfig } from "@/lib/app-config"
import { IdentityProvider, fetchPlatformUser, platformIdentity } from "@/lib/identity"
import { LanguageProvider } from "@/i18n"
import { ThemeProvider } from "@/providers/ThemeProvider"

async function start() {
  const config = await fetchAppConfig()
  document.title = config.title

  let AuthRoot: ({ children }: { children: ReactNode }) => ReactNode
  if (config.auth_mode === "platform") {
    // The platform already authenticated the request, so there is no login step to wait on
    const identity = platformIdentity(await fetchPlatformUser())
    AuthRoot = ({ children }) => <IdentityProvider value={identity}>{children}</IdentityProvider>
  } else {
    // Dynamic import: platform builds never load MSAL (its module reads VITE_MSAL_* at import)
    const { createMsalRoot } = await import("@/lib/msal-identity")
    AuthRoot = await createMsalRoot()
  }

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <AppConfigProvider value={config}>
        <AuthRoot>
          <LanguageProvider>
            <ThemeProvider>
              <App />
            </ThemeProvider>
          </LanguageProvider>
        </AuthRoot>
      </AppConfigProvider>
    </StrictMode>,
  )
}

void start()
